import React, { FC, useEffect, useState } from "react"
import Loading from "../../../ui/Loading"
import { KTitleSpan, KDropdown, KButton, KSpan, KInput, KTooltip } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { releases } from "../../../../apis"
import { State51Delivery } from "../../../../types"
import { checkAdminPermission, mapListToCamelCase } from "../../../../utility"
import Pagination from "../../../ui/table/Pagination"
import { toast } from "react-toastify"
import {
  ADMIN_PERMISSIONS,
  errorFilledToastOptions,
  STATE_51_DELIVERY_STATUSES,
  successFilledToastOptions
} from "../../../../constants"
import AdminState51DeliveryMenu from "./menu/AdminState51DeliveryMenu"
import { connect } from "react-redux"
import { RootState } from "../../../../store"
import SearchUserComponent from "../../../ui/components/SearchUserComponent"

const State51DeliveryOptionList: KSelectOption[] = [
  { label: "In Progress", value: 1, value2: STATE_51_DELIVERY_STATUSES.IN_PROGRESS },
  { label: "Not Found", value: 2, value2: STATE_51_DELIVERY_STATUSES.NOT_FOUND },
  { label: "Submit Error", value: 3, value2: STATE_51_DELIVERY_STATUSES.SUBMIT_ERROR },
  { label: "Submit Complete", value: 4, value2: STATE_51_DELIVERY_STATUSES.SUBMIT_COMPLETE },
  { label: "Delivery Error", value: 5, value2: STATE_51_DELIVERY_STATUSES.DELIVERY_ERROR },
  { label: "Delivery Complete", value: 6, value2: STATE_51_DELIVERY_STATUSES.DELIVERY_COMPLETE }
]

interface State51DeliveryFilterType {
  selectedStatus?: KSelectOption
  productId: string
  orderId: string
  batchId: string
  upc: string
  label: string
  genre: string
}

const initialState51DeliveryFilter: State51DeliveryFilterType = {
  selectedStatus: undefined,
  productId: "",
  orderId: "",
  batchId: "",
  upc: "",
  label: "",
  genre: ""
}

const columnWidths: any = {
  ID: "3%",
  CREATED: "7%",
  USER_ID: "5%",
  USER_NAME: "15%",
  ATTEMPT: "5.5%",
  STATUS: "9.5%",
  MESSAGE: "9%",
  NEXT_RUN: "7%",
  PRODUCT_ORDER_BATCH_ID: "14.5%",
  GENRE_LABEL: "8%",
  RELEASE_UPC_TITLE: "11%",
  ACTION: "5%"
}

type AdminState51DeliveryProps = {
  userAdminPermissions?: string
}

const AdminState51Delivery: FC<AdminState51DeliveryProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState<KSelectOption>()
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [state51DeliveryList, setState51DeliveryList] = useState<State51Delivery[]>([])
  const [count, setCount] = useState(0)
  const [filter, setFilter] = useState<State51DeliveryFilterType>({ ...initialState51DeliveryFilter })

  const searchParams = new URLSearchParams(location.search)

  const fetchState51DeliveryList = (filterProp?: State51DeliveryFilterType) => {
    const state51Filter = filterProp || filter
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const statusParam = state51Filter.selectedStatus ? `&status=${state51Filter.selectedStatus.value2}` : ""
    const productIdParam = state51Filter.productId ? `&product_id=${state51Filter.productId}` : ""
    const orderIdParam = state51Filter.orderId ? `&order_id=${state51Filter.orderId}` : ""
    const batchIdParam = state51Filter.batchId ? `&batch_id=${state51Filter.batchId}` : ""
    const upcParam = state51Filter.upc ? `&upc=${state51Filter.upc}` : ""
    const labelParam = state51Filter.label ? `&label=${state51Filter.label}` : ""
    const genreParam = state51Filter.genre ? `&genre=${state51Filter.genre}` : ""

    setLoading(true)
    releases(
      `/release/state_51_delivery_list/?offset=${offset}&limit=${limit}&order_by=-id${userIdParam}${statusParam}${productIdParam}${orderIdParam}${batchIdParam}${upcParam}${labelParam}${genreParam}`,
      "GET"
    ).then((resp) => {
      setState51DeliveryList(mapListToCamelCase(resp.data.results))
      setCount(resp.data.count)
      setLoading(false)
    })
  }

  useEffect(() => {
    if (searchParams && searchParams.get("upc")) {
      const filterToUpdate = { ...filter, upc: searchParams.get("upc") || "" }
      setFilter({ ...filterToUpdate })
      fetchState51DeliveryList(filterToUpdate)
    } else {
      fetchState51DeliveryList(filter)
    }
  }, [offset])

  const onClickPagination = (pageNumber: number) => {
    setOffset((pageNumber - 1) * limit)
  }

  const onResetDelivery = (state51Delivery: State51Delivery) => {
    if (!window.confirm("Are you sure you want to restart?")) return

    releases(`/release/reset_state_51_delivery/?release_id=${state51Delivery.releaseId}`, "PUT", {
      id: state51Delivery
    }).then((resp) => {
      if (!resp.data.success) {
        toast.error(`Error`, errorFilledToastOptions)
        return
      }
      toast.success(`Success`, successFilledToastOptions)
      fetchState51DeliveryList(filter)
    })
  }

  const renderState51Delivery = () => {
    return (
      <div className="pt-4 pb-4" style={{ display: "flex", flexDirection: "column" }}>
        <div className="flex pb-2" style={{ width: "100%" }}>
          {Object.keys(columnWidths).map((header) => {
            return (
              <div key={`header_${header}`} style={{ width: columnWidths[header] }}>
                <KSpan fontWeight={500} text={header.replaceAll("_", " ")} />
              </div>
            )
          })}
        </div>
        {state51DeliveryList.map((state51Delivery) => {
          return (
            <div style={{ width: "100%" }} className="pt-5">
              <div
                key={`takedown_${state51Delivery.id}`}
                className="flex pt-2"
                style={{ width: "100%", borderTop: "1px dashed grey" }}
              >
                <div style={{ width: columnWidths.ID }} className="flex gap-1 items-center">
                  <KSpan text={state51Delivery.id?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.CREATED }} className="flex gap-1 items-center">
                  <KSpan text={state51Delivery.created?.substring(0, 19).replace("T", " ") || "-"} />
                </div>
                <div
                  style={{ width: columnWidths.USER_ID }}
                  className="flex gap-1 cursor-pointer items-center"
                  onClick={() =>
                    window.open(
                      `https://artists.kahuna.io/admin/user-asset?user_id=${state51Delivery.userId}`,
                      "_blank"
                    )
                  }
                >
                  <KSpan text={state51Delivery.userId?.toString() || "-"} />
                  <img src="/reports_icons/link-external.svg" width={12} />
                </div>
                <div style={{ width: columnWidths.USER_NAME }} className="flex gap-1 items-center">
                  <KSpan text={state51Delivery.user?.username || "-"} />
                </div>

                <div style={{ width: columnWidths.ATTEMPT }} className="flex gap-1 items-center">
                  <KSpan text={state51Delivery.attempt.toString() || "-"} />
                </div>

                <div style={{ width: columnWidths.STATUS }} className="flex gap-1 items-center">
                  <KSpan text={state51Delivery.status || "-"} />
                </div>

                <div style={{ width: columnWidths.MESSAGE }} className="flex gap-1 items-center">
                  <KTooltip
                    width="350px"
                    position="bottom"
                    children={<KSpan text={`${state51Delivery.message?.substring(0, 15) || "-"}...`} />}
                    content={<KSpan text={state51Delivery.message || "-"} />}
                  />
                </div>

                <div style={{ width: columnWidths.NEXT_RUN }} className="flex gap-1 items-center">
                  <KSpan text={state51Delivery.nextRun?.substring(0, 19).replace("T", " ") || "-"} />
                </div>

                <div style={{ width: columnWidths.PRODUCT_ORDER_BATCH_ID }} className="flex flex-col items-center">
                  <KSpan text={state51Delivery.productId || "-"} />
                  <KSpan text={state51Delivery.orderId || "-"} />
                  <KSpan text={state51Delivery.batchId || "-"} />
                </div>

                <div style={{ width: columnWidths.GENRE_LABEL }} className="flex flex-col gap-1">
                  <KTooltip
                    width="180px"
                    position="bottom"
                    children={<KSpan text={`${state51Delivery.genre?.substring(0, 10) || "-"}...`} />}
                    content={<KSpan text={state51Delivery.genre || "-"} />}
                  />
                  <KTooltip
                    width="180px"
                    position="bottom"
                    children={<KSpan text={`${state51Delivery.label?.substring(0, 10) || "-"}...`} />}
                    content={<KSpan text={state51Delivery.label || "-"} />}
                  />
                </div>

                <div style={{ width: columnWidths.RELEASE_UPC_TITLE }} className="flex flex-col gap-1">
                  <div
                    className="cursor-pointer"
                    onClick={() =>
                      window.open(
                        `https://artists.kahuna.io/admin/catalog?upc=${state51Delivery.release?.upc}`,
                        "_blank"
                      )
                    }
                  >
                    <KSpan text={state51Delivery.release?.upc || "-"} />
                  </div>
                  <KTooltip
                    width="180px"
                    position="bottom"
                    children={<KSpan text={`${state51Delivery.release?.title?.substring(0, 27) || "-"}...`} />}
                    content={<KSpan text={state51Delivery.release?.title || "-"} />}
                  />
                </div>

                <div style={{ width: columnWidths.ACTION }} className="flex gap-1 items-center">
                  {checkAdminPermission(ADMIN_PERMISSIONS.STATE_51_DELIVERIES_WRITE, props.userAdminPermissions) && (
                    <AdminState51DeliveryMenu
                      state51Delivery={state51Delivery}
                      fetchState51DeliveryList={fetchState51DeliveryList}
                      onResetDelivery={onResetDelivery}
                    />
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        <KTitleSpan fontSize={20} lineHeight="28px" text={"State 51 Deliveries"} />
        <div className="flex gap-2">
          <div>
            <KSpan text="Product Id" />
            <KInput
              placeholder="Type.."
              value={filter.productId}
              onChange={(value) => setFilter({ ...filter, productId: value })}
            />
          </div>
          <div>
            <KSpan text="Order Id" />
            <KInput
              placeholder="Type.."
              value={filter.orderId}
              onChange={(value) => setFilter({ ...filter, orderId: value })}
            />
          </div>
          <div>
            <KSpan text="Batch Id" />
            <KInput
              placeholder="Type.."
              value={filter.batchId}
              onChange={(value) => setFilter({ ...filter, batchId: value })}
            />
          </div>
          <div>
            <KSpan text="UPC" />
            <KInput
              placeholder="Type.."
              value={filter.upc}
              onChange={(value) => setFilter({ ...filter, upc: value })}
            />
          </div>
          <div>
            <KSpan text="Label" />
            <KInput
              placeholder="Type.."
              value={filter.label}
              onChange={(value) => setFilter({ ...filter, label: value })}
            />
          </div>
          <div>
            <KSpan text="Genre" />
            <KInput
              placeholder="Type.."
              value={filter.genre}
              onChange={(value) => setFilter({ ...filter, genre: value })}
            />
          </div>
          <div>
            <KSpan text="Status" />
            <KDropdown
              width={180}
              isClearable
              selected={filter.selectedStatus}
              options={State51DeliveryOptionList}
              onSelect={(selected) => setFilter({ ...filter, selectedStatus: selected as KSelectOption })}
              placeholder="All"
            />
          </div>
          <div>
            <KSpan text="User" />
            <SearchUserComponent className="" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
          </div>
          <div className="mt-6">
            <KButton
              height="35px"
              text="Apply Filters"
              background="black"
              textColor="white"
              onClick={() => fetchState51DeliveryList()}
            />
          </div>
        </div>
      </div>
      {renderState51Delivery()}
      <div>
        <Pagination
          key={""}
          hidden={false}
          size={"sm"}
          pageCount={Math.ceil(count / limit)}
          currentPage={offset / limit + 1}
          onClickPage={onClickPagination}
        />
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    userAdminPermissions: state.auth.user.adminPermissions
  }
}

export default connect(mapStateToProps, {})(AdminState51Delivery)
